/* global cashflowForecastDataUrl hasOpeningBalance cashflowSettings cashflowEvent cashflowSettingsFetchUrl cashflowSettingsSaveUrl cashflowSettingsTaxLinesUrl cashflowSettingsTaxLinesDataUrl cashflowForecastExportUrl */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import App from 'App';
import axios from 'axios';
import { format, parse, parseISO, isBefore } from 'date-fns';

import { CashflowSettingsScreen } from '../cashflow-settings/CashflowSettingsScreen';

import CashflowForecastReportTable from './CashflowForecastReportTable';
import { CashflowForecastReportTableProvider, useCashflowForecastTableContext } from './CashflowForecastReportTableContext';
import CommitmentViewControl from './CommitmentViewControl';

import Button from 'CommonComponents/Button';
import DatePickerField from 'CommonComponents/DatePickerField';
import LoadingSpinner from 'CommonComponents/LoadingSpinner';
import ModalFooter from 'CommonComponents/ModalFooter';
import ModalHeader from 'CommonComponents/ModalHeader';
import Tabs, { RenderStrategyOnFirstVisit } from 'CommonComponents/Tabs';

import useModal from 'Hooks/useModal';

import CircleInfoIcon from 'Assets/icons/circle-info.svg';
import FileExportIcon from 'Assets/icons/file-export.svg';

/**
 * Format a Date to 'YYYY-MM' for the data query param.
 */
function formatMonthParam(date) {
    if (!date) return null;
    return format(date, 'yyyy-MM');
}

/**
 * Parse a month key in "mm-YYYY" format to a Date object (first of that month).
 */
function parseMonthKey(monthKey) {
    return parse(monthKey, 'MM-yyyy', new Date());
}

function HeaderControls({ startMonth, onStartMonthChange, minDate, maxDate }) {
    return (
        <div className="tw-flex tw-items-center tw-gap-2">
            <span className="tw-text-sm tw-text-charcoal-50 tw-whitespace-nowrap">{$t('Start month')}</span>
            <DatePickerField
                name="start_month"
                value={startMonth ? format(startMonth, 'yyyy-MM-dd') : null}
                onChange={(_name, val) => onStartMonthChange(val)}
                dateFormat="MM-yyyy"
                showMonthYearPicker
                min={minDate}
                max={maxDate}
                className="text-input tw-w-32"
                valueFormat="yyyy-MM-dd"
            />
        </div>
    );
}

function SetupPrompt() {
    return (
        <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-16 tw-px-6 tw-text-center">
            <p className="tw-text-charcoal tw-mb-4">
                {$t('Cashflow settings have not been configured. Please set the opening balance in the Inputs tab first.')}
            </p>
        </div>
    );
}

function CommitmentViewSwitch() {
    const { view, setView } = useCashflowForecastTableContext();

    return <CommitmentViewControl value={view} onChange={setView} />;
}

function ExpandCollapseButtons() {
    const { expandAll, collapseAll } = useCashflowForecastTableContext();
    return (
        <div className="tw-flex tw-items-center tw-gap-1.5">
            <span className="tw-text-xs tw-font-medium tw-text-charcoal-50">{$t('Row view')}</span>
            <button className="tw-text-xs tw-font-medium tw-text-charcoal tw-underline" onClick={expandAll}>
                {$t('Expand all')}
            </button>
            <button className="tw-text-xs tw-font-medium tw-text-charcoal tw-underline" onClick={collapseAll}>
                {$t('Collapse all')}
            </button>
        </div>
    );
}

function ResetOverridesButton() {
    const { overriddenMonths, resetAllOverrides } = useCashflowForecastTableContext();
    const modal = useModal();

    if (overriddenMonths.size === 0) return null;

    const handleConfirm = () => {
        resetAllOverrides();
        modal.hide();
    };

    return (
        <>
            <Button variant="primary" small onClick={modal.show}>
                {$t('Reset overrides')}
            </Button>

            <div ref={modal.ref} tabIndex="-1" aria-hidden="true" className="hidden modal__box tw-z-50">
                <div className="modal__container">
                    <div className="modal__inner-container">
                        <ModalHeader title={$t('Reset overrides')} close={modal.hide} />
                        <div className="tw-rounded-b">
                            <div className="modal__form-container">
                                <p className="modal__msg modal__text-color">
                                    {$t('Are you sure you want to reset all closing balance overrides? This cannot be undone.')}
                                </p>
                                <ModalFooter
                                    submitButtonVariant="danger"
                                    cancelButtonVariant="secondary"
                                    primaryButtonText={$t('Confirm')}
                                    cancelButtonText={$t('Cancel')}
                                    onCancel={modal.hide}
                                    handleSubmit={handleConfirm}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

/**
 * Modal that blocks the start-month change when existing overrides would be discarded.
 * Calls onConfirm (which deletes the affected overrides + fetches new data) or onCancel.
 */
function StartMonthChangeModal({ modal, affectedMonthKeys, onConfirm, onCancel }) {
    const count = affectedMonthKeys.length;

    return (
        <div ref={modal.ref} tabIndex="-1" aria-hidden="true" className="hidden modal__box tw-z-50">
            <div className="modal__container">
                <div className="modal__inner-container">
                    <ModalHeader title={$t('Change start month')} close={onCancel} />
                    <div className="tw-rounded-b">
                        <div className="modal__form-container">
                            <p className="modal__msg modal__text-color">
                                {$t(
                                    'Changing the start month will discard {{count}} closing balance override(s) that fall before the new start month. This cannot be undone.',
                                    { count },
                                )}
                            </p>
                            <ModalFooter
                                submitButtonVariant="danger"
                                cancelButtonVariant="secondary"
                                primaryButtonText={$t('Confirm')}
                                cancelButtonText={$t('Cancel')}
                                onCancel={onCancel}
                                handleSubmit={onConfirm}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function CashflowForecastReportInner({ refetchRef }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [settingsMissing, setSettingsMissing] = useState(!hasOpeningBalance);

    const { syncOverriddenMonths, overriddenMonths, deleteOverridesForMonths } = useCashflowForecastTableContext();

    // Derive default start month from cashflowSettings.as_at_date or today
    const defaultStartDate = cashflowSettings && cashflowSettings.as_at_date ? parseISO(cashflowSettings.as_at_date) : new Date();
    const [startMonth, setStartMonth] = useState(defaultStartDate);
    const startMonthRef = useRef(startMonth);

    // Date bounds from the event
    const minDate = cashflowSettings && cashflowSettings.as_at_date ? cashflowSettings.as_at_date : null;
    const maxDate = cashflowEvent && cashflowEvent.end_date ? cashflowEvent.end_date : null;

    // Pending start-month change state — when overrides would be discarded we pause here
    const [pendingStartMonth, setPendingStartMonth] = useState(null);
    const [affectedMonthKeys, setAffectedMonthKeys] = useState([]);
    const startMonthChangeModal = useModal();

    const fetchData = useCallback(
        (monthDate) => {
            setLoading(true);
            setError(null);

            const param = formatMonthParam(monthDate);
            const url = param ? `${cashflowForecastDataUrl}?start_month=${param}` : cashflowForecastDataUrl;

            axios
                .get(url)
                .then((res) => {
                    setData(res.data);
                    setSettingsMissing(false);
                    setLoading(false);

                    // Sync overridden months from fresh server data
                    if (res.data && res.data.rows && res.data.rows.monthlyTotals) {
                        syncOverriddenMonths(res.data.rows.monthlyTotals);
                    }
                })
                .catch((err) => {
                    if (err.response && err.response.status === 422) {
                        const code = err.response.data && err.response.data.code;
                        if (code === 'cashflow_settings_missing') {
                            setSettingsMissing(true);
                            setLoading(false);
                            return;
                        }
                    }
                    setError((err.response && err.response.data && err.response.data.message) || $t('An error occurred while loading the cashflow forecast.'));
                    setLoading(false);
                });
        },
        [syncOverriddenMonths],
    );

    // Expose fetchData to the parent via ref so provider's onRefetch can call it
    useEffect(() => {
        if (refetchRef) {
            refetchRef.current = () => fetchData(startMonthRef.current);
        }
    }, [fetchData, refetchRef]);

    useEffect(() => {
        if (!settingsMissing) {
            fetchData(startMonth);
        }
    }, []);

    /**
     * Apply the start month change: update state and fetch new data.
     */
    const applyStartMonthChange = useCallback(
        (date) => {
            startMonthRef.current = date;
            setStartMonth(date);
            fetchData(date);
        },
        [fetchData],
    );

    const handleStartMonthChange = useCallback(
        (val) => {
            const date = val ? (val instanceof Date ? val : parseISO(val)) : null;

            if (!date) {
                applyStartMonthChange(date);
                return;
            }

            // Find any override month keys that fall strictly before the new start month
            const outOfWindow = Array.from(overriddenMonths).filter((monthKey) => {
                const monthDate = parseMonthKey(monthKey);
                return isBefore(monthDate, date);
            });

            if (outOfWindow.length > 0) {
                // Block the change — show confirmation modal
                setPendingStartMonth(date);
                setAffectedMonthKeys(outOfWindow);
                startMonthChangeModal.show();
            } else {
                applyStartMonthChange(date);
            }
        },
        [applyStartMonthChange, overriddenMonths, startMonthChangeModal],
    );

    const handleStartMonthChangeConfirm = useCallback(() => {
        startMonthChangeModal.hide();

        deleteOverridesForMonths(affectedMonthKeys).then(() => {
            applyStartMonthChange(pendingStartMonth);
            setPendingStartMonth(null);
            setAffectedMonthKeys([]);
        });
    }, [startMonthChangeModal, deleteOverridesForMonths, affectedMonthKeys, applyStartMonthChange, pendingStartMonth]);

    const handleStartMonthChangeCancel = useCallback(() => {
        startMonthChangeModal.hide();
        setPendingStartMonth(null);
        setAffectedMonthKeys([]);
        // startMonth state is unchanged — the picker will revert to the current value
    }, [startMonthChangeModal]);

    const handleSettingsSaved = useCallback(() => {
        setSettingsMissing(false);
        fetchData(startMonthRef.current);
    }, [fetchData]);

    const tabs = useMemo(
        () => [
            {
                id: 'cashflow-forecast---inputs',
                title: $t('Inputs'),
                content: () => (
                    <>
                        <div className="tw-flex tw-items-center tw-mb-4 tw-gap-2 tw-pt-4">
                            <h4 className="tw-font-bold tw-text-charcoal tw-text-lg tw-mb-0">{$t('Cashflow Settings')}</h4>
                        </div>
                        <CashflowSettingsScreen
                            fetchUrl={cashflowSettingsFetchUrl}
                            saveUrl={cashflowSettingsSaveUrl}
                            taxLinesUrl={cashflowSettingsTaxLinesUrl}
                            taxLinesDataUrl={cashflowSettingsTaxLinesDataUrl}
                            onSaved={handleSettingsSaved}
                        />
                    </>
                ),
            },
            {
                id: 'cashflow-forecast---report',
                title: $t('Report'),
                content: () => (
                    <>
                        {settingsMissing && <SetupPrompt />}

                        {!settingsMissing && (
                            <>
                                <div className="tw-flex tw-items-center tw-justify-between tw-py-4">
                                    <div className="tw-flex tw-items-center tw-gap-2">
                                        <h4 className="tw-font-bold tw-text-charcoal tw-text-lg tw-mb-0">{$t('Cashflow Forecast')}</h4>
                                        <CircleInfoIcon className="tw-w-4 tw-h-4 tw-text-charcoal-50" />
                                    </div>
                                    <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
                                        <CommitmentViewSwitch />
                                        <HeaderControls
                                            startMonth={startMonth}
                                            onStartMonthChange={handleStartMonthChange}
                                            minDate={minDate}
                                            maxDate={maxDate}
                                        />
                                        <ResetOverridesButton />
                                        <Button
                                            variant="primary"
                                            small
                                            disabled={loading || !!error || !data || !data.months || data.months.length === 0}
                                            onClick={() => {
                                                window.location.href = `${cashflowForecastExportUrl}?start_month=${formatMonthParam(startMonth) || ''}`;
                                            }}
                                        >
                                            <FileExportIcon className="tw-w-3 tw-h-3" />
                                            {$t('Export to Excel')}
                                        </Button>
                                    </div>
                                </div>

                                {loading && <LoadingSpinner />}

                                {!loading && error && <div className="tw-text-red-600 tw-text-sm tw-py-4">{error}</div>}

                                {!loading && !error && data && data.months && data.months.length === 0 && (
                                    <div className="tw-text-charcoal-50 tw-text-sm tw-py-4">{$t('No forecast period available.')}</div>
                                )}

                                {!loading && !error && data && data.months && data.months.length > 0 && (
                                    <>
                                        <div className="tw-mb-2 tw-flex tw-items-center tw-justify-between">
                                            <ExpandCollapseButtons />
                                        </div>
                                        <CashflowForecastReportTable data={data} />
                                    </>
                                )}
                            </>
                        )}
                    </>
                ),
            },
        ],
        [settingsMissing, startMonth, handleStartMonthChange, handleSettingsSaved, minDate, maxDate, loading, error, data],
    );

    return (
        <div className="tw-flex tw-flex-col tw-min-h-0 tw-h-full">
            <Tabs tabs={tabs} renderStrategy={RenderStrategyOnFirstVisit} />
            <StartMonthChangeModal
                modal={startMonthChangeModal}
                affectedMonthKeys={affectedMonthKeys}
                onConfirm={handleStartMonthChangeConfirm}
                onCancel={handleStartMonthChangeCancel}
            />
        </div>
    );
}

export default function CashflowForecastReport() {
    // refetchRef is set by the inner component so that provider's onRefetch can trigger it
    const refetchRef = useRef(null);

    return (
        <CashflowForecastReportTableProvider onRefetch={() => refetchRef.current && refetchRef.current()}>
            <CashflowForecastReportInner refetchRef={refetchRef} />
        </CashflowForecastReportTableProvider>
    );
}

if (document.getElementById('cashflow-forecast-report')) {
    var rootEl = document.getElementById('cashflow-forecast-report');
    ReactDOM.render(
        <App>
            <CashflowForecastReport />
        </App>,
        rootEl,
    );
}
